import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const GRADE_OPTIONS = [
    { value: 0, label: 'Grade R' },
    { value: 1, label: 'Grade 1' },
    { value: 2, label: 'Grade 2' },
    { value: 3, label: 'Grade 3' }
];

const TERM_OPTIONS = [1, 2, 3, 4];

const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f3f7f2 0%, #ffffff 45%)',
    padding: '24px'
};

const shellStyle = {
    maxWidth: '1240px',
    margin: '0 auto'
};

const panelStyle = {
    background: '#ffffff',
    border: '1px solid #d9e5da',
    borderRadius: '16px',
    boxShadow: '0 18px 45px rgba(33, 55, 38, 0.08)'
};

const buttonBaseStyle = {
    border: '1px solid #c6d6c8',
    borderRadius: '999px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const fieldStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #cad7cc',
    fontSize: '13px',
    boxSizing: 'border-box',
    background: '#ffffff'
};

const labelStyle = {
    display: 'block',
    fontSize: '12px',
    color: '#5a675c',
    marginBottom: '6px',
    fontWeight: 600
};

const taskActionButtonStyle = {
    border: '1px solid #d2ddd4',
    borderRadius: '999px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    background: '#ffffff'
};

function formatDateTime(value) {
    if (!value) {
        return 'Not customized yet';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
}

function getSelectedTermData(grades, gradeLevel, term) {
    const grade = grades.find((entry) => entry.gradeLevel === gradeLevel);
    if (!grade) {
        return { subjects: [] };
    }

    return grade.terms.find((entry) => entry.term === term) || { subjects: [] };
}

function calculateSubjectWeight(subject) {
    return subject.components.reduce((total, component) => {
        const value = Number(component.capsWeightPercentage || 0);
        return total + (Number.isNaN(value) ? 0 : value);
    }, 0);
}

function createTempTaskKey() {
    return `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function cloneGrades(previousGrades) {
    return JSON.parse(JSON.stringify(previousGrades));
}

export default function FoundationPhaseAdmin() {
    const [grades, setGrades] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState(1);
    const [selectedTerm, setSelectedTerm] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [metadata, setMetadata] = useState({
        mode: 'seeded',
        customizedAt: '',
        customizedBy: '',
        scope: 'school',
        schoolId: null,
        schoolName: ''
    });

    const token = localStorage.getItem('auth_token');
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 900 : false;

    const loadConfiguration = useCallback(async (preserveMessage = false) => {
        try {
            if (!preserveMessage) {
                setSuccess('');
            }
            setError('');
            setLoading(true);

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/foundation_phase_admin.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data?.success) {
                throw new Error(response.data?.error || 'Failed to load Foundation Phase configuration.');
            }

            setGrades(response.data.grades || []);
            setMetadata({
                mode: response.data.mode || 'seeded',
                customizedAt: response.data.customizedAt || '',
                customizedBy: response.data.customizedBy || '',
                scope: response.data.scope || 'school',
                schoolId: response.data.schoolId ?? null,
                schoolName: response.data.schoolName || ''
            });
        } catch (requestError) {
            setError(requestError.response?.data?.error || requestError.message || 'Failed to load Foundation Phase configuration.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);

    const selectedTermData = useMemo(
        () => getSelectedTermData(grades, selectedGrade, selectedTerm),
        [grades, selectedGrade, selectedTerm]
    );

    const updateSelectedTermData = (updater) => {
        setGrades((previousGrades) => {
            const nextGrades = cloneGrades(previousGrades);
            const grade = nextGrades.find((entry) => entry.gradeLevel === selectedGrade);
            if (!grade) {
                return previousGrades;
            }

            const term = grade.terms.find((entry) => entry.term === selectedTerm);
            if (!term) {
                return previousGrades;
            }

            updater(term);
            return nextGrades;
        });
    };

    const updateTaskField = (subjectCode, componentKey, taskKey, updater) => {
        updateSelectedTermData((term) => {
            const subject = term.subjects.find((entry) => entry.subjectCode === subjectCode);
            const component = subject?.components.find((entry) => entry.componentKey === componentKey);
            const task = component?.tasks.find((entry) => entry.taskKey === taskKey);
            if (task) {
                updater(task);
            }
        });
    };

    const handleWeightChange = (subjectCode, componentKey, value) => {
        updateSelectedTermData((term) => {
            const subject = term.subjects.find((entry) => entry.subjectCode === subjectCode);
            const component = subject?.components.find((entry) => entry.componentKey === componentKey);
            if (component) {
                component.capsWeightPercentage = value;
            }
        });
    };

    const handleReportScaleChange = (subjectCode, componentKey, value) => {
        updateSelectedTermData((term) => {
            const subject = term.subjects.find((entry) => entry.subjectCode === subjectCode);
            const component = subject?.components.find((entry) => entry.componentKey === componentKey);
            if (component) {
                component.reportScaleMax = value;
            }
        });
    };

    const handleTaskLabelChange = (subjectCode, componentKey, taskKey, value, isObservationBased) => {
        updateTaskField(subjectCode, componentKey, taskKey, (task) => {
            task.taskLabel = value;
            if (isObservationBased) {
                task.observationLabel = value;
            }
        });
    };

    const handleMaxMarkChange = (subjectCode, componentKey, taskKey, value) => {
        updateTaskField(subjectCode, componentKey, taskKey, (task) => {
            task.rawMaxScore = value;
        });
    };

    const handleRemoveTask = (subjectCode, componentKey, taskKey) => {
        updateSelectedTermData((term) => {
            const subject = term.subjects.find((entry) => entry.subjectCode === subjectCode);
            const component = subject?.components.find((entry) => entry.componentKey === componentKey);
            if (!component || component.tasks.length <= 1) {
                return;
            }

            component.tasks = component.tasks.filter((task) => task.taskKey !== taskKey);
        });
    };

    const handleAddTask = (subjectCode, componentKey, componentLabel, isObservationBased) => {
        updateSelectedTermData((term) => {
            const subject = term.subjects.find((entry) => entry.subjectCode === subjectCode);
            const component = subject?.components.find((entry) => entry.componentKey === componentKey);
            if (!component) {
                return;
            }

            const nextIndex = component.tasks.length + 1;
            const label = isObservationBased
                ? `${componentLabel} Observation ${nextIndex}`
                : `${componentLabel} Task ${nextIndex}`;

            component.tasks.push({
                taskKey: createTempTaskKey(),
                taskLabel: label,
                rawMaxScore: isObservationBased ? null : 10,
                observationLabel: isObservationBased ? label : null,
                displayOrder: nextIndex,
                isCustomTask: true,
                hasSchoolOverride: true
            });
        });
    };

    const handleSave = async () => {
        const subjects = selectedTermData.subjects || [];
        if (subjects.length === 0) {
            setError('There is nothing to save for this grade and term.');
            return;
        }

        if (selectedGrade !== 0) {
            const invalidSubject = subjects.find((subject) => Math.abs(calculateSubjectWeight(subject) - 100) > 0.01);
            if (invalidSubject) {
                setError(`${invalidSubject.subjectName} must total 100% before saving.`);
                return;
            }
        }

        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/foundation_phase_admin.php`,
                {
                    gradeLevel: selectedGrade,
                    term: selectedTerm,
                    subjects
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!response.data?.success) {
                throw new Error(response.data?.error || 'Failed to save Foundation Phase configuration.');
            }

            setGrades(response.data.grades || []);
            setMetadata({
                mode: response.data.mode || 'custom',
                customizedAt: response.data.customizedAt || '',
                customizedBy: response.data.customizedBy || '',
                scope: response.data.scope || 'school',
                schoolId: response.data.schoolId ?? null,
                schoolName: response.data.schoolName || ''
            });
            setSuccess(`Saved school-specific Foundation Phase overrides for ${GRADE_OPTIONS.find((option) => option.value === selectedGrade)?.label || 'the selected grade'} Term ${selectedTerm}.`);
        } catch (requestError) {
            setError(requestError.response?.data?.error || requestError.message || 'Failed to save Foundation Phase configuration.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Reset this school back to the default Foundation Phase CAPS weights, task max marks, and Grade R task labels?')) {
            return;
        }

        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/foundation_phase_admin.php`,
                { action: 'reset_defaults' },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!response.data?.success) {
                throw new Error(response.data?.error || 'Failed to reset Foundation Phase configuration.');
            }

            setGrades(response.data.grades || []);
            setMetadata({
                mode: response.data.mode || 'seeded',
                customizedAt: response.data.customizedAt || '',
                customizedBy: response.data.customizedBy || '',
                scope: response.data.scope || 'school',
                schoolId: response.data.schoolId ?? null,
                schoolName: response.data.schoolName || ''
            });
            setSuccess('This school is back on the default Foundation Phase configuration.');
        } catch (requestError) {
            setError(requestError.response?.data?.error || requestError.message || 'Failed to reset Foundation Phase configuration.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{
                ...pageStyle,
                marginLeft: isMobile ? 0 : 220,
                minHeight: 'calc(100vh - 64px)',
                padding: isMobile ? '20px' : '32px'
            }}
        >
            <div style={shellStyle}>
                <div style={{ ...panelStyle, padding: '28px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '30px', color: '#203327' }}>Foundation Phase Admin</h1>
                            <p style={{ margin: '10px 0 0 0', color: '#617066', maxWidth: '760px', lineHeight: 1.6 }}>
                                Manage the live Foundation Phase template used by your school. CAPS components stay fixed, but you can set school-specific weights, max marks, and add or remove tasks inside each component.
                            </p>
                        </div>
                        <div style={{ padding: '12px 16px', borderRadius: '14px', background: '#eff6ef', border: '1px solid #d6e4d8', minWidth: '260px' }}>
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6a796d', marginBottom: '6px' }}>Configuration Scope</div>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: '#203327' }}>
                                {metadata.schoolName ? `${metadata.schoolName} only` : 'Current school only'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#667468', marginTop: '8px' }}>
                                Mode: <strong>{metadata.mode === 'custom' ? 'School override active' : 'Using seeded defaults'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ ...panelStyle, padding: '20px', marginBottom: '20px', background: 'linear-gradient(135deg, #fff8e7 0%, #fffcf3 100%)', borderColor: '#f0ddaa' }}>
                    <strong style={{ display: 'block', color: '#6f5311', marginBottom: '6px' }}>Important</strong>
                    <span style={{ color: '#765c1d', lineHeight: 1.6, fontSize: '14px' }}>
                        These overrides affect Foundation Phase subjects for this school only. Removing a managed task here will also remove its managed section from Foundation Phase subjects using that template in this database.
                    </span>
                </div>

                <div style={{ ...panelStyle, padding: '20px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {GRADE_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setSelectedGrade(option.value)}
                                    style={{
                                        ...buttonBaseStyle,
                                        background: selectedGrade === option.value ? '#24462d' : '#f6faf6',
                                        color: selectedGrade === option.value ? '#ffffff' : '#26412d',
                                        borderColor: selectedGrade === option.value ? '#24462d' : '#cedacf'
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {TERM_OPTIONS.map((term) => (
                                <button
                                    key={term}
                                    type="button"
                                    onClick={() => setSelectedTerm(term)}
                                    style={{
                                        ...buttonBaseStyle,
                                        background: selectedTerm === term ? '#dceadf' : '#ffffff',
                                        color: '#26412d'
                                    }}
                                >
                                    Term {term}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ marginTop: '18px', display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#5e6d61', lineHeight: 1.6 }}>
                            Last customised: <strong>{formatDateTime(metadata.customizedAt)}</strong>
                            {metadata.customizedBy ? ` by ${metadata.customizedBy}` : ''}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => loadConfiguration(true)}
                                disabled={loading || saving}
                                style={{ ...buttonBaseStyle, background: '#ffffff', color: '#26412d' }}
                            >
                                Refresh
                            </button>
                            <button
                                type="button"
                                onClick={handleReset}
                                disabled={loading || saving}
                                style={{ ...buttonBaseStyle, background: '#fff1ef', color: '#8c2c23', borderColor: '#e5bab5' }}
                            >
                                Reset This School
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading || saving}
                                style={{ ...buttonBaseStyle, background: '#24462d', color: '#ffffff', borderColor: '#24462d' }}
                            >
                                {saving ? 'Saving...' : 'Save School Overrides'}
                            </button>
                        </div>
                    </div>
                </div>

                {error && (
                    <div style={{ ...panelStyle, padding: '14px 18px', marginBottom: '16px', borderColor: '#efc3c3', background: '#fff5f5', color: '#8b2f2f' }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{ ...panelStyle, padding: '14px 18px', marginBottom: '16px', borderColor: '#cce3cf', background: '#f3fbf4', color: '#235c31' }}>
                        {success}
                    </div>
                )}

                {loading ? (
                    <div style={{ ...panelStyle, padding: '40px', textAlign: 'center', color: '#5f6f63' }}>
                        Loading Foundation Phase configuration...
                    </div>
                ) : selectedTermData.subjects.length === 0 ? (
                    <div style={{ ...panelStyle, padding: '40px', textAlign: 'center', color: '#5f6f63' }}>
                        No Foundation Phase configuration is defined for this grade and term yet.
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '18px' }}>
                        {selectedTermData.subjects.map((subject) => {
                            const numericWeightTotal = calculateSubjectWeight(subject);

                            return (
                                <div key={subject.subjectCode} style={{ ...panelStyle, padding: '22px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
                                        <div>
                                            <h2 style={{ margin: 0, color: '#203327', fontSize: '22px' }}>{subject.subjectName}</h2>
                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6a776d', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                                {subject.subjectCode}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                padding: '12px 14px',
                                                borderRadius: '14px',
                                                minWidth: '180px',
                                                background: subject.isObservationBased ? '#eef6ff' : '#eef7f0',
                                                border: `1px solid ${subject.isObservationBased ? '#d1def1' : '#d1e4d4'}`
                                            }}
                                        >
                                            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b786e', marginBottom: '6px' }}>
                                                {subject.isObservationBased ? 'Observation Mode' : 'Weight Total'}
                                            </div>
                                            <div style={{ fontSize: '20px', fontWeight: 700, color: '#213328' }}>
                                                {subject.isObservationBased ? `${subject.components.reduce((total, component) => total + component.tasks.length, 0)} labels` : `${numericWeightTotal.toFixed(2)}%`}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#627065', marginTop: '6px' }}>
                                                {subject.isObservationBased ? 'Grade R averages rubric results per term.' : 'Every numeric subject should stay at 100%.'}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gap: '16px' }}>
                                        {subject.components.map((component) => (
                                            <div key={component.componentKey} style={{ border: '1px solid #e1e9e2', borderRadius: '14px', overflow: 'hidden' }}>
                                                <div style={{ padding: '16px 18px', background: '#f8fbf8', borderBottom: '1px solid #e1e9e2' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: subject.isObservationBased ? '1fr auto' : 'minmax(0, 1fr) 160px 160px auto', gap: '16px', alignItems: 'end' }}>
                                                        <div>
                                                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#203327' }}>{component.componentLabel}</div>
                                                            <div style={{ marginTop: '4px', fontSize: '12px', color: '#69776c' }}>
                                                                {subject.isObservationBased
                                                                    ? `${component.tasks.length} observation label${component.tasks.length === 1 ? '' : 's'}`
                                                                    : `Raw total: ${component.tasks.reduce((total, task) => total + Number(task.rawMaxScore || 0), 0)} | Report mark max: ${component.reportScaleMax ?? component.defaultMaxMark ?? 0}`}
                                                            </div>
                                                        </div>

                                                        {!subject.isObservationBased && (
                                                            <div>
                                                                <label style={labelStyle}>Component Weight (%)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    max="100"
                                                                    step="0.01"
                                                                    value={component.capsWeightPercentage ?? ''}
                                                                    onChange={(event) => handleWeightChange(subject.subjectCode, component.componentKey, event.target.value)}
                                                                    style={fieldStyle}
                                                                />
                                                            </div>
                                                        )}

                                                        {!subject.isObservationBased && (
                                                            <div>
                                                                <label style={labelStyle}>Report Mark Max</label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    step="1"
                                                                    value={component.reportScaleMax ?? ''}
                                                                    onChange={(event) => handleReportScaleChange(subject.subjectCode, component.componentKey, event.target.value)}
                                                                    style={fieldStyle}
                                                                />
                                                            </div>
                                                        )}

                                                        <button
                                                            type="button"
                                                            onClick={() => handleAddTask(subject.subjectCode, component.componentKey, component.componentLabel, subject.isObservationBased)}
                                                            style={{ ...taskActionButtonStyle, background: '#eef7f0', color: '#235c31', borderColor: '#cfe2d2', whiteSpace: 'nowrap' }}
                                                        >
                                                            + Add {subject.isObservationBased ? 'Label' : 'Task'}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div style={{ padding: '16px 18px', display: 'grid', gap: '14px' }}>
                                                    {component.tasks.map((task) => (
                                                        <div
                                                            key={task.taskKey}
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: subject.isObservationBased ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr) 180px auto',
                                                                gap: '16px',
                                                                alignItems: 'end'
                                                            }}
                                                        >
                                                            <div>
                                                                <label style={labelStyle}>
                                                                    {subject.isObservationBased ? 'Observation Label' : 'Task Label'}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={subject.isObservationBased ? (task.observationLabel || task.taskLabel || '') : (task.taskLabel || '')}
                                                                    onChange={(event) => handleTaskLabelChange(subject.subjectCode, component.componentKey, task.taskKey, event.target.value, subject.isObservationBased)}
                                                                    style={fieldStyle}
                                                                />
                                                            </div>

                                                            {!subject.isObservationBased && (
                                                                <div>
                                                                    <label style={labelStyle}>Max Mark</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        value={task.rawMaxScore ?? ''}
                                                                        onChange={(event) => handleMaxMarkChange(subject.subjectCode, component.componentKey, task.taskKey, event.target.value)}
                                                                        style={fieldStyle}
                                                                    />
                                                                </div>
                                                            )}

                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveTask(subject.subjectCode, component.componentKey, task.taskKey)}
                                                                disabled={component.tasks.length <= 1}
                                                                style={{
                                                                    ...taskActionButtonStyle,
                                                                    background: component.tasks.length <= 1 ? '#f6f7f6' : '#fff3f1',
                                                                    color: component.tasks.length <= 1 ? '#98a39a' : '#8c2c23',
                                                                    borderColor: component.tasks.length <= 1 ? '#e1e6e2' : '#e5bab5',
                                                                    cursor: component.tasks.length <= 1 ? 'not-allowed' : 'pointer',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
